import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiUpload, FiX, FiImage } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import './InterfaceSettings.css';

const InterfaceSettings = () => {
  const { isModerator } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [companyTitle, setCompanyTitle] = useState('');
  const [settings, setSettings] = useState({
    logo: null,
    logo_url: null,
    website_url: '',
    primary_color: '#ff6b35',
    secondary_color: '#ff8c5a',
    accent_color: '#ffa07a',
    timestamp_theory_color: '#d1ecf1',
    timestamp_practice_color: '#fff3cd',
    timestamp_theory_badge_color: '#17a2b8',
    timestamp_practice_badge_color: '#ffc107',
    schedule_from_email: '',
    default_registration_url: '',
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_use_ssl: true,
    smtp_use_tls: false,
  });
  const [smtpPasswordInput, setSmtpPasswordInput] = useState('');
  const [smtpPasswordConfigured, setSmtpPasswordConfigured] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsCount, setEmailLogsCount] = useState(0);
  const [emailLogFilters, setEmailLogFilters] = useState({
    status: 'all',
    category: 'all',
    recipient: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    if (isModerator) {
      axios.get('/api/companies/').then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setCompanies(list);
        if (list.length) {
          loadCompanySettings(list[0].id);
        } else {
          fetchSettingsCurrent();
        }
      }).catch(() => fetchSettingsCurrent());
    } else {
      fetchSettingsCurrent();
    }
  }, [isModerator]);

  useEffect(() => {
    if (!isModerator || !companyId) return;
    fetchEmailDeliveryLogs(1);
  }, [isModerator, companyId]);

  const applyCompanyData = (data) => {
      setCompanyId(data.id);
      setCompanyTitle(data.name || '');
      setSettings({
        logo: null,
        logo_url: data.logo_url || null,
        website_url: data.website_url || '',
        primary_color: data.primary_color || '#ff6b35',
        secondary_color: data.secondary_color || '#ff8c5a',
        accent_color: data.accent_color || '#ffa07a',
        timestamp_theory_color: data.timestamp_theory_color || '#d1ecf1',
        timestamp_practice_color: data.timestamp_practice_color || '#fff3cd',
        timestamp_theory_badge_color: data.timestamp_theory_badge_color || '#17a2b8',
        timestamp_practice_badge_color: data.timestamp_practice_badge_color || '#ffc107',
        schedule_from_email: data.schedule_from_email || '',
        default_registration_url: data.default_registration_url || '',
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 465,
        smtp_user: data.smtp_user || '',
        smtp_use_ssl: data.smtp_use_ssl !== false,
        smtp_use_tls: Boolean(data.smtp_use_tls),
      });
      setSmtpPasswordInput('');
      setSmtpPasswordConfigured(Boolean(data.smtp_password_set));
      setLogoPreview(data.logo_url || null);
      setLogoRemoved(false);
  };

  const loadCompanySettings = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/companies/${id}/branding/`);
      applyCompanyData(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки настроек фирмы');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettingsCurrent = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/companies/current/');
      applyCompanyData(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки настроек интерфейса');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Размер файла не должен превышать 5 МБ');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Выберите файл изображения');
        return;
      }
      setSettings({ ...settings, logo: file });
      setLogoRemoved(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setSettings({ ...settings, logo: null, logo_url: null });
    setLogoPreview(null);
    setLogoRemoved(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      
      if (settings.logo) {
        // Новый логотип
        formData.append('logo', settings.logo);
      } else if (logoRemoved) {
        // Если логотип был удален пользователем, отправляем пустое значение
        formData.append('logo', '');
      }
      // Если logo_url существует и logo не установлен и не был удален - ничего не отправляем (оставляем текущий)
      
      formData.append('website_url', settings.website_url);
      formData.append('primary_color', settings.primary_color);
      formData.append('secondary_color', settings.secondary_color);
      formData.append('accent_color', settings.accent_color);
      formData.append('timestamp_theory_color', settings.timestamp_theory_color);
      formData.append('timestamp_practice_color', settings.timestamp_practice_color);
      formData.append('timestamp_theory_badge_color', settings.timestamp_theory_badge_color);
      formData.append('timestamp_practice_badge_color', settings.timestamp_practice_badge_color);
      formData.append('schedule_from_email', settings.schedule_from_email || '');
      formData.append('default_registration_url', settings.default_registration_url || '');
      formData.append('smtp_host', settings.smtp_host || '');
      formData.append('smtp_port', String(settings.smtp_port || 465));
      formData.append('smtp_user', settings.smtp_user || '');
      formData.append('smtp_use_ssl', settings.smtp_use_ssl ? 'true' : 'false');
      formData.append('smtp_use_tls', settings.smtp_use_tls ? 'true' : 'false');
      if (smtpPasswordInput.trim()) {
        formData.append('smtp_password', smtpPasswordInput.trim());
      }

      if (!companyId) {
        toast.error('Фирма не определена для текущего домена');
        return;
      }
      await axios.put(`/api/companies/${companyId}/branding/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Настройки оформления фирмы сохранены');
      if (companyId && isModerator) {
        loadCompanySettings(companyId);
      } else {
        fetchSettingsCurrent();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const fetchEmailDeliveryLogs = async (nextPage = emailLogsPage) => {
    if (!isModerator || !companyId) return;
    setEmailLogsLoading(true);
    try {
      const params = {
        page: nextPage,
        page_size: 30,
        company_id: companyId,
      };
      if (emailLogFilters.status !== 'all') params.status = emailLogFilters.status;
      if (emailLogFilters.category !== 'all') params.category = emailLogFilters.category;
      if (emailLogFilters.recipient.trim()) params.recipient = emailLogFilters.recipient.trim();
      if (emailLogFilters.date_from) params.date_from = emailLogFilters.date_from;
      if (emailLogFilters.date_to) params.date_to = emailLogFilters.date_to;
      const response = await axios.get('/api/settings/email-delivery-logs/', { params });
      const data = response.data || {};
      setEmailLogs(Array.isArray(data.results) ? data.results : []);
      setEmailLogsCount(data.count || 0);
      setEmailLogsPage(nextPage);
    } catch (error) {
      toast.error('Ошибка загрузки логов отправки почты');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="interface-settings-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="interface-settings-page">
      <div className="page-header">
        <div>
          <h1>Настройки интерфейса</h1>
          {isModerator && companies.length > 1 && (
            <div className="interface-company-picker">
              <label htmlFor="interface-company-id">Редактировать фирму:</label>
              <select
                id="interface-company-id"
                className="form-select"
                value={companyId || ''}
                onChange={(e) => loadCompanySettings(Number(e.target.value))}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.domain})</option>
                ))}
              </select>
            </div>
          )}
          {companyTitle && (
            <p className="interface-company-subtitle">
              Фирма: <strong>{companyTitle}</strong>
              {isModerator ? ' — брендинг и почта для выбранной фирмы' : ' — для текущего домена'}
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <div className="spinner-small"></div> Сохранение...
            </>
          ) : (
            <>
              <FiSave /> Сохранить
            </>
          )}
        </button>
      </div>

      <div className="settings-sections">
        {/* Логотип */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Логотип</h2>
          </div>
          <div className="settings-form">
            <div className="form-group">
              <label className="form-label">Логотип (будет отображаться в правом верхнем углу)</label>
              <div className="logo-upload-area">
                {logoPreview ? (
                  <div className="logo-preview">
                    <img src={logoPreview} alt="Логотип" />
                    <button
                      type="button"
                      className="btn-remove-logo"
                      onClick={handleRemoveLogo}
                      title="Удалить логотип"
                    >
                      <FiX />
                    </button>
                  </div>
                ) : (
                  <label className="logo-upload-label">
                    <FiImage className="upload-icon" />
                    <span>Загрузить логотип</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
              <div className="form-hint">
                Рекомендуемый размер: до 200x60 пикселей. Форматы: PNG, JPG, SVG. Максимальный размер: 5 МБ.
              </div>
            </div>
          </div>
        </div>

        {/* Ссылка на сайт */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Ссылка на веб-сайт</h2>
          </div>
          <div className="settings-form">
            <div className="form-group">
              <label className="form-label">URL веб-сайта (при нажатии на логотип будет переход на этот адрес)</label>
              <input
                type="url"
                className="form-input"
                value={settings.website_url}
                onChange={(e) => setSettings({ ...settings, website_url: e.target.value })}
                placeholder="https://example.com"
              />
              <div className="form-hint">
                Оставьте пустым, если не хотите, чтобы логотип был кликабельным
              </div>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <h2>Почта и расписание</h2>
          </div>
          <div className="settings-form">
            <p className="form-hint section-intro-hint">
              Если SMTP сервер заполнен — письма этой фирмы (расписание, рассылка вебинаров, «Поделиться») идут через него.
              Если пусто — используется общий SMTP из .env сервера.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">SMTP сервер</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.smtp_host}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="mail.example.com"
                />
              </div>
              <div className="form-group form-group-narrow">
                <label className="form-label">Порт</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  max={65535}
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: Number(e.target.value) || 465 })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">SMTP логин</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.smtp_user}
                  onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                  placeholder="user@example.com"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label className="form-label">SMTP пароль</label>
                <input
                  type="password"
                  className="form-input"
                  value={smtpPasswordInput}
                  onChange={(e) => setSmtpPasswordInput(e.target.value)}
                  placeholder={smtpPasswordConfigured ? '•••••••• (оставьте пустым, чтобы не менять)' : 'Пароль SMTP'}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="form-row form-row-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.smtp_use_ssl}
                  onChange={(e) => setSettings({
                    ...settings,
                    smtp_use_ssl: e.target.checked,
                    smtp_use_tls: e.target.checked ? false : settings.smtp_use_tls,
                  })}
                />
                SSL (обычно порт 465)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.smtp_use_tls}
                  onChange={(e) => setSettings({
                    ...settings,
                    smtp_use_tls: e.target.checked,
                    smtp_use_ssl: e.target.checked ? false : settings.smtp_use_ssl,
                  })}
                />
                TLS (обычно порт 587)
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Email отправителя (From)</label>
              <input
                type="email"
                className="form-input"
                value={settings.schedule_from_email}
                onChange={(e) => setSettings({ ...settings, schedule_from_email: e.target.value })}
                placeholder="schedule@example.com"
              />
              <div className="form-hint">
                Поле From в письмах. Если пусто — используется SMTP логин или системный email.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Ссылка на регистрацию по умолчанию</label>
              <input
                type="url"
                className="form-input"
                value={settings.default_registration_url}
                onChange={(e) => setSettings({ ...settings, default_registration_url: e.target.value })}
                placeholder="https://..."
              />
              <div className="form-hint">
                Для этой фирмы — своя ссылка на запись. Подставляется в новые события и в письма, если у события нет своей ссылки.
              </div>
            </div>
          </div>
        </div>

        {isModerator && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Логи отправки почты</h2>
            </div>
            <div className="settings-form">
              <p className="form-hint section-intro-hint">
                Статус «Отправлено» означает, что SMTP-сервер принял письмо. Фактическая доставка зависит от почтового провайдера получателя.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Статус</label>
                  <select
                    className="form-input"
                    value={emailLogFilters.status}
                    onChange={(e) => setEmailLogFilters({ ...emailLogFilters, status: e.target.value })}
                  >
                    <option value="all">Все</option>
                    <option value="queued">В очереди</option>
                    <option value="sent">Отправлено</option>
                    <option value="failed">Ошибка</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Тип</label>
                  <select
                    className="form-input"
                    value={emailLogFilters.category}
                    onChange={(e) => setEmailLogFilters({ ...emailLogFilters, category: e.target.value })}
                  >
                    <option value="all">Все</option>
                    <option value="schedule">Расписание</option>
                    <option value="webinar">Вебинар</option>
                    <option value="share">Публичная ссылка</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Email получателя</label>
                  <input
                    type="text"
                    className="form-input"
                    value={emailLogFilters.recipient}
                    onChange={(e) => setEmailLogFilters({ ...emailLogFilters, recipient: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">С даты</label>
                  <input
                    type="date"
                    className="form-input"
                    value={emailLogFilters.date_from}
                    onChange={(e) => setEmailLogFilters({ ...emailLogFilters, date_from: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">По дату</label>
                  <input
                    type="date"
                    className="form-input"
                    value={emailLogFilters.date_to}
                    onChange={(e) => setEmailLogFilters({ ...emailLogFilters, date_to: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">&nbsp;</label>
                  <button
                    className="btn btn-outline"
                    type="button"
                    onClick={() => fetchEmailDeliveryLogs(1)}
                    disabled={emailLogsLoading}
                  >
                    Обновить
                  </button>
                </div>
              </div>

              <div className="email-logs-table-wrap">
                <table className="table email-logs-table">
                  <thead>
                    <tr>
                      <th>Время</th>
                      <th>Тип</th>
                      <th>Статус</th>
                      <th>Получатель</th>
                      <th>Тема</th>
                      <th>Вебинар</th>
                      <th>Батч</th>
                      <th>Ошибка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogsLoading ? (
                      <tr><td colSpan="8">Загрузка...</td></tr>
                    ) : emailLogs.length > 0 ? (
                      emailLogs.map((row) => (
                        <tr key={row.id}>
                          <td>{new Date(row.created_at).toLocaleString('ru-RU')}</td>
                          <td>{row.category_display || row.category}</td>
                          <td>
                            <span className={`email-log-status status-${row.status}`}>
                              {row.status_display || row.status}
                            </span>
                          </td>
                          <td>{row.recipient_email}</td>
                          <td>{row.subject || '—'}</td>
                          <td>{row.webinar_title || '—'}</td>
                          <td>{row.batch_number}/{row.total_batches}</td>
                          <td>{row.error_message || '—'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="8">Нет записей по выбранным фильтрам</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="email-logs-paging">
                <span>Всего: {emailLogsCount}</span>
                <div className="email-logs-paging-controls">
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={emailLogsPage <= 1 || emailLogsLoading}
                    onClick={() => fetchEmailDeliveryLogs(Math.max(1, emailLogsPage - 1))}
                  >
                    Назад
                  </button>
                  <span>Страница {emailLogsPage}</span>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={emailLogsPage * 30 >= emailLogsCount || emailLogsLoading}
                    onClick={() => fetchEmailDeliveryLogs(emailLogsPage + 1)}
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Цвета */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Цветовая схема</h2>
          </div>
          <div className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Основной цвет</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    placeholder="#ff6b35"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Вторичный цвет</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                    placeholder="#ff8c5a"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Акцентный цвет</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.accent_color}
                    onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.accent_color}
                    onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                    placeholder="#ffa07a"
                  />
                </div>
              </div>
            </div>
            <div className="color-preview">
              <div className="preview-item" style={{ backgroundColor: settings.primary_color }}>
                <span>Основной цвет</span>
              </div>
              <div className="preview-item" style={{ backgroundColor: settings.secondary_color }}>
                <span>Вторичный цвет</span>
              </div>
              <div className="preview-item" style={{ backgroundColor: settings.accent_color }}>
                <span>Акцентный цвет</span>
              </div>
            </div>
          </div>
        </div>

        {/* Страница входа */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Дизайн страницы входа</h2>
          </div>
          <div className="settings-form">
            <p className="form-hint">
              Страница входа использует основную цветовую схему (градиент из основного и вторичного цвета) и логотип из настроек выше. Измените «Основной» и «Вторичный» цвет, чтобы настроить фон страницы входа.
            </p>
          </div>
        </div>

        {/* Цвета временных меток */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Цвета временных меток</h2>
          </div>
          <div className="settings-form">
            <p className="form-hint" style={{ marginBottom: '16px' }}>
              Цвета блоков «Теория» и «Практика» на странице вебинара и в карточках.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Теория (фон)</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.timestamp_theory_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_theory_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.timestamp_theory_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_theory_color: e.target.value })}
                    placeholder="#d1ecf1"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Теория (бейдж)</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.timestamp_theory_badge_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_theory_badge_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.timestamp_theory_badge_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_theory_badge_color: e.target.value })}
                    placeholder="#17a2b8"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Практика (фон)</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.timestamp_practice_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_practice_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.timestamp_practice_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_practice_color: e.target.value })}
                    placeholder="#fff3cd"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Практика (бейдж)</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    className="color-input"
                    value={settings.timestamp_practice_badge_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_practice_badge_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input color-text-input"
                    value={settings.timestamp_practice_badge_color}
                    onChange={(e) => setSettings({ ...settings, timestamp_practice_badge_color: e.target.value })}
                    placeholder="#ffc107"
                  />
                </div>
              </div>
            </div>
            <div className="color-preview">
              <div className="preview-item" style={{ backgroundColor: settings.timestamp_theory_color }}>
                <span style={{ color: '#333' }}>Теория</span>
              </div>
              <div className="preview-item" style={{ backgroundColor: settings.timestamp_practice_color }}>
                <span style={{ color: '#333' }}>Практика</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterfaceSettings;
